/**
 * Collapsed-card headline-KPI previews (PLT-4997).
 *
 * After PLT-4997 made every MCP-app card collapse by default, a stack of
 * collapsed cards is a tidy list of `chevron + label + Show` rows — which
 * fixes the wall-of-tables complaint but leaves a bare label that only lets
 * the user *identify* a card, not *compare* it. So on intermediate+ surfaces
 * (the CSS gates this — see `.chat-mcp-app-card-preview` in
 * `upright-chat-theme.css`) the collapsed card also shows its headline figure
 * right-aligned next to the Show affordance, so a stack of e.g. DMA cards
 * reads as a scannable column of single figures without anyone expanding
 * anything. On the narrow side panel / mobile the figure is hidden and the row
 * stays minimal.
 *
 * `getMcpAppCardPreview` is a pure `(appName, toolResult) -> segments | null`
 * map. Each preview is a SHORT list of `{ label, value, tone? }` pairs — in
 * practice a single figure per card (`{ label: 'Net impact ratio', value:
 * '+49%', tone: 'positive' }`, `{ label: 'Material matters', value: '9/16' }`,
 * …) so a stack reads as one labelled, right-aligned figure per row. The
 * `label` names the metric and is shown next to the value (Janne: "add labels
 * to the values so we know what they mean"); the `value` is the glanceable
 * scalar; the optional `tone` sign-colours valence-carrying figures (net
 * impact) — see `upright-chat-theme.css`.
 *
 * `toolResult` is the `result.data` payload handed to `ChatMcpAppIframe` —
 * i.e. the same success/no-data payload the iframe renders, typed here via the
 * shared `@upright/common/mcp-payloads/*` schemas (the single source of truth
 * shared with the backend tools). `null` means "no preview" — render the bare
 * label — and is returned for:
 *   - any app we deliberately don't preview (comparison / DD / value-chain
 *     cards stay expanded anyway, so a collapsed-only preview never shows;
 *     and apps without one obvious headline scalar);
 *   - no-data payloads (`dataAvailable === false`);
 *   - malformed / still-streaming payloads (defensive — the preview is
 *     cosmetic and must never throw or block the card from rendering).
 *
 * Keep each preview to a SHORT set of labelled scalars (one per card today).
 * The moment a preview turns into a mini-table of many rows we've reinvented
 * the wall PLT-4997 removed.
 */
import type { ContributingProductsSuccessPayload } from '@upright/common/mcp-payloads/contributing-products';
import type { DmaResultSuccessPayload } from '@upright/common/mcp-payloads/dma-result';
import type { NetImpactSuccessPayload } from '@upright/common/mcp-payloads/net-impact';
import type { SfdrPaisSuccessPayload } from '@upright/common/mcp-payloads/sfdr-pais';
import type { SingleMatterSuccessPayload } from '@upright/common/mcp-payloads/single-matter';
import type { SupportingArticlesSuccessPayload } from '@upright/common/mcp-payloads/supporting-articles';

/**
 * One labelled headline figure in a collapsed-card preview. `label` names the
 * metric ("Net impact ratio", "Material matters", …) and is shown alongside the
 * value so the figure is self-explanatory; `value` is the glanceable scalar
 * ("+49%", "9/16", "Material") the renderer shows right-aligned. `tone` is the
 * optional valence for sign-carrying figures (today only net impact): the
 * renderer tints `positive` / `negative` values with the chat's valence colours
 * — the same `--valence__*-strong` tokens that sign-colour numbers in markdown
 * tables and KPI tiles — while figures with no positive/negative meaning (ratios
 * like `9/16`, counts) carry no tone and stay neutral near-black.
 */
export interface McpAppCardPreviewSegment {
  label: string;
  value: string;
  /**
   * Optional secondary "reference" part of a ratio figure — the `/total`
   * denominator (e.g. `/16` in a `9/16` material-matters ratio). Split out from
   * `value` so the renderer can show it a touch lighter than the numerator (the
   * count that matters), letting the total read as context (Janne, PLT-4997).
   * Omitted for non-ratio figures (net impact `+49%`, counts, verdicts).
   */
  reference?: string;
  tone?: 'positive' | 'negative';
}

/**
 * Build a ratio preview segment (`material/total`) with the denominator split
 * into `reference` so the renderer can de-emphasise it — e.g. `9` + `/16`. The
 * numerator is the count that matters; the total is shown lighter as context.
 */
function ratioSegment(label: string, material: number, total: number): McpAppCardPreviewSegment {
  return { label, value: `${material}`, reference: `/${total}` };
}

/** Narrow `unknown` to a plain object so field reads below are type-safe. */
function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

/**
 * True for a no-data payload (`dataAvailable === false`). Success payloads
 * mostly omit the field (treated as "available"); `contributing-products` is
 * the one that sets `dataAvailable: true` explicitly — both are non-`false`,
 * so this single check covers every app.
 */
function isNoData(data: Record<string, unknown>): boolean {
  return data.dataAvailable === false;
}

/** Signed, whole-percent net impact ratio — matches the card's own headline (`+49%`, `-3%`). */
function netImpactPreview(data: Record<string, unknown>): McpAppCardPreviewSegment[] | null {
  const { netScore } = data as Partial<NetImpactSuccessPayload>;
  if (typeof netScore !== 'number' || !Number.isFinite(netScore)) return null;
  const pct = Math.round(netScore * 100);
  // Net impact ratio carries valence (Janne, PLT-4997): a net-positive company
  // tints positive, net-negative tints negative. Exactly zero (`+0%`) is
  // neither, so it stays neutral (no tone) rather than mislabelling it positive.
  const tone = pct > 0 ? 'positive' : pct < 0 ? 'negative' : undefined;
  return [{ label: 'Net impact ratio', value: `${pct >= 0 ? '+' : ''}${pct}%`, tone }];
}

/**
 * A single material/total matter ratio (`9/16`), so the DMA card reads with
 * the same one-figure shape as Net impact (`+49%`) and SFDR PAIs (`12/18`)
 * rather than a four-cell per-category strip — the collapsed stack stays a
 * tidy column of single right-aligned figures (PLT-4997, Janne's "material
 * matters as 9/XX, like net impact and the PAI"). Uses the always-present
 * top-level `materialMatters`/`totalMatters` rollup; falls back to summing the
 * per-category counts only for payloads that somehow omit the rollup.
 */
function dmaResultPreview(data: Record<string, unknown>): McpAppCardPreviewSegment[] | null {
  const { materialMatters, totalMatters, categories } = data as Partial<DmaResultSuccessPayload>;
  if (typeof materialMatters === 'number' && typeof totalMatters === 'number') {
    return [ratioSegment('Material matters', materialMatters, totalMatters)];
  }
  if (Array.isArray(categories) && categories.length > 0) {
    const material = categories.reduce((sum, c) => sum + c.materialCount, 0);
    const total = categories.reduce((sum, c) => sum + c.totalCount, 0);
    return [ratioSegment('Material matters', material, total)];
  }
  return null;
}

/** Matter-level double-materiality rollup — the single matter's key verdict. */
function singleMatterPreview(data: Record<string, unknown>): McpAppCardPreviewSegment[] | null {
  const status = (data as Partial<SingleMatterSuccessPayload>).matterResult?.materialityStatus;
  if (status === 'material') return [{ label: 'Materiality', value: 'Material' }];
  if (status === 'not-material') return [{ label: 'Materiality', value: 'Not material' }];
  return null;
}

/** Count of contributing products listed. */
function contributingProductsPreview(data: Record<string, unknown>): McpAppCardPreviewSegment[] | null {
  const { products } = data as Partial<ContributingProductsSuccessPayload>;
  if (!Array.isArray(products)) return null;
  return [{ label: 'Contributing products', value: `${products.length}` }];
}

/** Count of supporting articles found (the backend's truthful total, not the page size). */
function supportingArticlesPreview(data: Record<string, unknown>): McpAppCardPreviewSegment[] | null {
  const { totalArticles } = data as Partial<SupportingArticlesSuccessPayload>;
  if (typeof totalArticles !== 'number') return null;
  return [{ label: 'Supporting articles', value: `${totalArticles}` }];
}

/** SFDR PAI coverage as covered/available indicators. */
function sfdrPaisPreview(data: Record<string, unknown>): McpAppCardPreviewSegment[] | null {
  const { metricsCovered, metricsAvailable } = data as Partial<SfdrPaisSuccessPayload>;
  if (typeof metricsCovered !== 'number' || typeof metricsAvailable !== 'number') return null;
  return [ratioSegment('PAI indicators', metricsCovered, metricsAvailable)];
}

/**
 * Map an MCP app's `appName` + `toolResult` to a collapsed-card preview
 * (an array of labelled headline figures), or `null` for no preview. See the
 * module header for what `null` covers and the keep-it-short rule.
 */
export function getMcpAppCardPreview(appName: string, toolResult: unknown): McpAppCardPreviewSegment[] | null {
  const data = asRecord(toolResult);
  if (!data || isNoData(data)) return null;

  switch (appName) {
    case 'net-impact':
      return netImpactPreview(data);
    case 'dma-result':
      return dmaResultPreview(data);
    case 'single-matter':
      return singleMatterPreview(data);
    case 'contributing-products':
      return contributingProductsPreview(data);
    case 'supporting-articles':
      return supportingArticlesPreview(data);
    case 'sfdr-pais':
      return sfdrPaisPreview(data);
    // Comparison (multi-company-*), DD lifecycle (company-card,
    // product-set-card) and value-chain cards default to expanded, so a
    // collapsed-only preview would never show — and SDG / EU-Taxonomy /
    // financial-effects have no single unambiguous headline scalar. All
    // fall through to no preview.
    default:
      return null;
  }
}
